import React, { useEffect, useRef } from 'react';

const NeuralActivity = ({ isActive = true, className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    const particles = [];
    const particleCount = 30;
    const connectionRadius = canvas.width / 6; // Adjust for responsiveness

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = (Math.random() * 1 - 0.5) * 0.5;
        this.speedY = (Math.random() * 1 - 0.5) * 0.5;
        this.color = `rgba(158, 62, 255, ${Math.random() * 0.5 + 0.2})`; // accent-purple variants
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function initParticles() {
      particles.length = 0; // Clear existing particles
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    }
    initParticles(); // Initial particle creation

    function connectParticles() {
      for (let a = 0; a < particles.length; a++) {
        for (let b = a; b < particles.length; b++) {
          const dx = particles[a].x - particles[b].x;
          const dy = particles[a].y - particles[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionRadius) {
            const opacity = 1 - distance / connectionRadius;
            ctx.strokeStyle = `rgba(60, 169, 255, ${opacity * 0.5})`; // accent-blue for connections
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
    }
    
    let pulseFactor = 0;
    let pulseDirection = 1;

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Pulsing background effect
      pulseFactor += 0.01 * pulseDirection;
      if (pulseFactor > 1 || pulseFactor < 0) pulseDirection *= -1;
      
      const gradient = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, 0,
          canvas.width / 2, canvas.height / 2, canvas.width / 2 + pulseFactor * 50
      );
      gradient.addColorStop(0, `rgba(158, 62, 255, ${0.05 + pulseFactor * 0.05})`); // accent-purple subtle glow
      gradient.addColorStop(1, 'rgba(158, 62, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.update();
        p.draw();
      });
      connectParticles();
      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isActive]); // Rerun effect if isActive changes

  if (!isActive) {
    return (
        <div className={`h-full w-full bg-dark-700 rounded-lg flex items-center justify-center ${className}`}>
            <p className="text-xs text-gray-500">Neural Activity Paused</p>
        </div>
    );
  }

  return (
    <canvas ref={canvasRef} className={`h-full w-full rounded-lg ${className}`}></canvas>
  );
};

export default NeuralActivity;